import { API_BASE_URL } from './config';
import { apiFetch } from './apiFetch';

export type AIJobType = 'report' | 'extraction' | 'analysis' | 'embedding';
export type AIJobStatus = 'pending' | 'processing' | 'completed' | 'failed';

export type AIJob = {
  id: string;
  type: AIJobType;
  status: AIJobStatus;
  progress: number;
  payload: unknown;
  result?: AIReportResult | AIExtractionResult | AIAnalysisResult;
  error?: string;
  createdAt: string;
  updatedAt: string;
};

export type AIReportResult = {
  type: 'report';
  title: string;
  generatedAt: string;
  summary: string;
  sections: Array<{ heading: string; content: string }>;
  recommendations: string[];
  confidenceScore: number;
};

export type AIExtractionResult = {
  type: 'extraction';
  extractedAt: string;
  competencies: Array<{ code: string; level: number; confidence: number }>;
};

export type AIAnalysisResult = {
  type: 'analysis';
  analyzedAt: string;
  insights: string[];
  riskFlags: string[];
  developmentPriority: 'high' | 'medium' | 'low';
  retentionRisk: 'high' | 'medium' | 'low';
};

export async function listAIJobs() {
  const response = await apiFetch(`${API_BASE_URL}/api/ai-queue/jobs`, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.json() as Promise<{ success: boolean; data?: { jobs: AIJob[] }; error?: string }>;
}

export async function createAIJob(type: AIJobType, payload: Record<string, unknown>) {
  const response = await apiFetch(`${API_BASE_URL}/api/ai-queue/jobs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type, payload }),
  });
  return response.json() as Promise<{ success: boolean; data?: { job: AIJob }; error?: string }>;
}

export async function getAIJob(id: string) {
  const response = await apiFetch(`${API_BASE_URL}/api/ai-queue/jobs/${id}`, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.json() as Promise<{ success: boolean; data?: { job: AIJob }; error?: string }>;
}

export async function processAIJob(id: string) {
  const response = await apiFetch(`${API_BASE_URL}/api/ai-queue/jobs/${id}/process`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  return response.json() as Promise<{ success: boolean; data?: { job: AIJob }; error?: string }>;
}
